/**
 * The band (04 §5): how each part of the score sounds. An early-80s arcade board, not a home
 * computer: three clean voices and no tricks. The lead is a bright quarter-width pulse, plucked
 * and then held, with a little vibrato on long notes; the second line is a hollow square under
 * it; chords are played broken, a soft triangle note on every step; the bass is a round triangle
 * with a quiet square inside it for small speakers. The drums take the bass voice's turns. Pure.
 */

import { midiToHz } from '../notes';
import { adsr, type VoiceSpec } from '../voice';
import type { Drum, Mix, NoteEvent } from './types';

/** A little air before the next note: the bounce. */
const GAP = 0.02;

function gateFor(ev: NoteEvent, stepSeconds: number): number {
  return Math.max(0.02, ev.len * stepSeconds - GAP);
}

function lead(hz: number, gate: number, level: number): VoiceSpec[] {
  return [{
    wave: 'pulse', at: 0, freq: hz, gate, level,
    env: adsr(0.003, 0.16, 0.6, 0.07),
    width: 0.25,
    // Short notes stay straight; held ones start to sing after a moment.
    vibrato: { rate: 6.2, depth: 16, delay: Math.min(0.2, gate * 0.5) },
  }];
}

function alto(hz: number, gate: number, level: number): VoiceSpec[] {
  return [{
    wave: 'pulse', at: 0, freq: hz, gate, level,
    env: adsr(0.003, 0.14, 0.5, 0.06),
    width: 0.5,
  }];
}

/** The chord broken upward, one plucked note a step; a triad gets its octave so the figure is four long. */
function arp(notes: readonly number[], len: number, stepSeconds: number, level: number): VoiceSpec[] {
  const root = notes[0] ?? 60;
  const cycle = notes.length === 3 ? [...notes, root + 12] : notes;
  return Array.from({ length: len }, (_, i): VoiceSpec => ({
    wave: 'triangle', at: i * stepSeconds, freq: midiToHz(cycle[i % cycle.length] ?? root), level,
    gate: Math.max(0.02, stepSeconds - GAP),
    env: adsr(0.002, 0.09, 0.25, 0.04),
  }));
}

function bass(hz: number, gate: number, level: number): VoiceSpec[] {
  return [
    { wave: 'triangle', at: 0, freq: hz, gate, level, env: adsr(0.002, 0.2, 0.6, 0.05) },
    {
      wave: 'pulse', at: 0, freq: hz, gate, level: level * 0.3,
      env: adsr(0.002, 0.1, 0.3, 0.04),
      width: 0.5,
      filter: { type: 'lowpass', freq: 900, q: 0 },
    },
  ];
}

function drum(kind: Drum, level: number): VoiceSpec[] {
  // Struck: each gate lasts its decay, so the release does not cut the decay short.
  switch (kind) {
    case 'kick':
      return [{
        wave: 'triangle', at: 0, freq: 160, to: 45, glide: 0.06, gate: 0.12, env: adsr(0.001, 0.12, 0, 0.03), level,
      }];
    case 'snare':
      return [
        {
          wave: 'noise', at: 0, freq: 5000, to: 2000, glide: 0.1, gate: 0.12, env: adsr(0.001, 0.12, 0, 0.03),
          level: level * 0.8,
        },
        {
          wave: 'triangle', at: 0, freq: 240, to: 160, glide: 0.05, gate: 0.06, env: adsr(0.001, 0.06, 0, 0.02),
          level: level * 0.4,
        },
      ];
    case 'hat':
      return [{
        wave: 'noise', at: 0, freq: 12000, gate: 0.03, env: adsr(0.001, 0.03, 0, 0.01), level: level * 0.4,
        filter: { type: 'highpass', freq: 7000, q: 0 },
      }];
  }
}

/** The voices for one event of the score. */
export function voicesFor(ev: NoteEvent, stepSeconds: number, mix: Mix): VoiceSpec[] {
  if (ev.drum) return drum(ev.drum, mix.drum);
  const gate = gateFor(ev, stepSeconds);
  const first = midiToHz(ev.notes[0] ?? 60);
  switch (ev.part) {
    case 'lead': return lead(first, gate, mix.lead);
    case 'alto': return alto(first, gate, mix.alto);
    case 'arp': return arp(ev.notes, ev.len, stepSeconds, mix.arp);
    case 'bass': return bass(first, gate, mix.bass);
    case 'drum': return [];
  }
}
